import { useState } from "react";
import { useSelector } from "react-redux";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import { RootState } from "../store";
import { useGetProjectsQuery, useGetProjectMembersQuery } from "../store/api/projects";
import { useCompleteProjectTaskMutation } from "../store/api/tasks";
import { TaskInstance, User, Project } from "../types";
import { isOverdue, getOverdueDays, getDateDiffInDays } from "../utils/taskGenerator";
import { Checkbox } from "./ui/checkbox";
import { Calendar, AlertCircle, Clock, CheckCircle2, Repeat } from "lucide-react";
import { format } from "date-fns";
import { ja, enUS } from "date-fns/locale";
import { WORKSPACE_DEFAULT_TIMEZONE } from "../utils/constants";
import TaskDetailModal from "./TaskDetailModal";
import CompleteTaskConfirmDialog from "./CompleteTaskConfirmDialog";
import UncompleteTaskConfirmDialog from "./UncompleteTaskConfirmDialog";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";

interface TaskListViewProps {
  tasks: TaskInstance[];
  users?: User[];  // Optional: pass users from parent, fallback to context
  projectMembers?: User[];  // Optional: project members for assignee selection in edit modal
  projects?: Project[];  // Optional: pass projects from parent, fallback to API
  onTaskUpdate?: () => void;  // Optional: callback when a task is updated (completed/uncompleted)
}

export default function TaskListView({ tasks, users: propUsers, projectMembers, projects: propProjects, onTaskUpdate }: TaskListViewProps) {
  const appContext = useApp();
  const { routineTasks, currentUser } = appContext;
  
  // Get current workspace from Redux
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const workspaceId = currentWorkspace?.id || '';
  const workspaceTimeZone = currentWorkspace?.timezone || WORKSPACE_DEFAULT_TIMEZONE;

  // Fetch projects from API if not provided
  const { data: apiProjects = [] } = useGetProjectsQuery(
    workspaceId,
    { skip: !workspaceId || !!propProjects }
  );
  
  // Use provided props or fallback to API/context
  const users = propUsers || appContext.users;
  const projects = propProjects || apiProjects;

  // API mutation for completing tasks
  const [completeProjectTask] = useCompleteProjectTaskMutation();

  const { t, language } = useLanguage();
  
  // Store selected task ID and the task object for the modal
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<TaskInstance | null>(null);
  const [confirmCompleteTask, setConfirmCompleteTask] = useState<TaskInstance | null>(null);
  const [confirmUncompleteTask, setConfirmUncompleteTask] = useState<TaskInstance | null>(null);

  // Fetch project members for the selected task's project (dynamic fetch)
  const { data: selectedTaskProjectMembers = [] } = useGetProjectMembersQuery(
    { workspaceId, projectId: selectedTask?.projectId || '' },
    { skip: !workspaceId || !selectedTask?.projectId || !!projectMembers }
  );
  
  // Use provided projectMembers prop or dynamically fetched members
  const effectiveProjectMembers = projectMembers || selectedTaskProjectMembers;

  const dateLocale = language === 'ja' ? ja : enUS;

  const getRepeatLabel = (routineTaskId: string | null, taskRepeatPattern?: any) => {
    // If task has repeatPattern directly, use it
    if (taskRepeatPattern) {
      const pattern = taskRepeatPattern;
      
      switch (pattern.type) {
        case "none":
          return t('repeatPattern.none');
        case "daily":
          return t('repeatPattern.daily');
        case "weekly":
          if (pattern.specificDays && pattern.specificDays.length > 0) {
            const days = language === 'ja' 
              ? ["月", "火", "水", "木", "金", "土", "日"]
              : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            const dayLabels = pattern.specificDays.map((d: number) => days[d - 1]).join(language === 'ja' ? "・" : ", ");
            return `${t('repeatPattern.weekly')}(${dayLabels})`;
          }
          return t('repeatPattern.weekly');
        case "monthly":
          if (pattern.specificDate === "last") {
            return language === 'ja' ? "月次(月末)" : "Monthly (Last Day)";
          } else if (pattern.specificDate) {
            return language === 'ja' ? `月次(${pattern.specificDate}日)` : `Monthly (Day ${pattern.specificDate})`;
          }
          return t('repeatPattern.monthly');
        case "quarterly":
          return language === 'ja' ? "四半期" : "Quarterly";
        case "semi-annually":
          return language === 'ja' ? "半期" : "Semi-annually";
        case "yearly":
          return t('repeatPattern.yearly');
        default:
          return null;
      }
    }
    
    // Fallback: look up in routineTasks if pattern not provided directly
    if (routineTaskId) {
      const routineTask = routineTasks.find((rt) => rt.id === routineTaskId);
      if (!routineTask) return null;
      const pattern = (routineTask as any).repeatPattern;
      
      switch (pattern.type) {
        case "none":
          return t('repeatPattern.none');
        case "daily":
          return t('repeatPattern.daily');
        case "weekly":
          if (pattern.specificDays && pattern.specificDays.length > 0) {
            const days = language === 'ja' 
              ? ["月", "火", "水", "木", "金", "土", "日"]
              : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            const dayLabels = pattern.specificDays.map((d: any) => days[d - 1]).join(language === 'ja' ? "・" : ", ");
            return `${t('repeatPattern.weekly')}(${dayLabels})`;
          }
          return t('repeatPattern.weekly');
        case "monthly":
          if (pattern.specificDate === "last") {
            return language === 'ja' ? "月次(月末)" : "Monthly (Last Day)";
          } else if (pattern.specificDate) {
            return language === 'ja' ? `月次(${pattern.specificDate}日)` : `Monthly (Day ${pattern.specificDate})`;
          }
          return t('repeatPattern.monthly');
        case "quarterly":
          return language === 'ja' ? "四半期" : "Quarterly";
        case "semi-annually":
          return language === 'ja' ? "半期" : "Semi-annually";
        case "yearly":
          return t('repeatPattern.yearly');
        default:
          return null;
      }
    }
    
    return null;
  };

  const getTaskPriorityColor = (task: TaskInstance, timeZone: string) => {
    if (task.status === "completed") return "text-gray-500";
    
    if (isOverdue(task, timeZone)) return "text-red-600";
    
    const diffDays = getDateDiffInDays(task.dueDate, new Date(), timeZone);
    
    if (diffDays === 0) return "text-orange-600";
    if (diffDays <= 7) return "text-yellow-600";
    return "text-green-600";
  };

  const getTaskBorderColor = (task: TaskInstance, timeZone: string) => {
    if (task.status === "completed") return "border-gray-200";
    
    if (isOverdue(task, timeZone)) return "border-red-200 bg-red-50";
    
    const diffDays = getDateDiffInDays(task.dueDate, new Date(), timeZone);
    
    if (diffDays === 0) return "border-orange-200 bg-orange-50";
    if (diffDays <= 7) return "border-yellow-200 bg-yellow-50";
    return "border-gray-200";
  };

  const handleToggleComplete = async (task: TaskInstance) => {
    if (task.status === "completed") {
      // Show confirmation dialog before uncompleting
      setConfirmUncompleteTask(task);
    } else {
      // Show confirmation dialog before completing
      setConfirmCompleteTask(task);
    }
  };

  const handleTaskClick = (task: TaskInstance) => {
    setSelectedTaskId(task.id);
    setSelectedTask(task);
  };

  const handleCloseModal = () => {
    setSelectedTaskId(null);
    setSelectedTask(null);
  };

  if (tasks.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">{language === 'ja' ? '該当するタスクがありません' : 'No matching tasks'}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {tasks.map((task: any) => {
          const project = projects.find((p) => p.id === task.projectId);
          const assignees = users.filter((u) => task.assigneeIds.includes(u.id));
          const priorityColor = getTaskPriorityColor(task, workspaceTimeZone);
          const borderColor = getTaskBorderColor(task, workspaceTimeZone);
          const overdueDays = getOverdueDays(task, workspaceTimeZone);
          // Try to get repeatPattern from task directly, fallback to looking up in routineTasks
          const repeatLabel = (task as any).repeatPattern 
            ? getRepeatLabel(task.routineTaskId, (task as any).repeatPattern)
            : getRepeatLabel(task.routineTaskId);

          return (
            <div
              key={task.id}
              className={`bg-white rounded-lg border p-4 transition-all hover:shadow-md cursor-pointer ${borderColor}`}
              onClick={() => handleTaskClick(task)}
            >
              <div className="flex items-start gap-4">
                {/* チェックボックス */}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleComplete(task);
                  }}
                >
                  <Checkbox
                    checked={task.status === "completed"}
                    className="mt-1"
                  />
                </div>

                {/* タスク情報 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3
                          className={`text-gray-900 ${
                            task.status === "completed" ? "line-through text-gray-500" : ""
                          }`}
                        >
                          {task.name}
                        </h3>
                        {project && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs flex-shrink-0">
                            {project.name}
                          </span>
                        )}
                      </div>
                      {task.description && (
                        <p className="text-gray-600 text-sm">{task.description}</p>
                      )}
                    </div>

                    {/* 期限表示 */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <div className={`flex items-center gap-1.5 ${priorityColor}`}>
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">
                          {format(task.dueDate, "M/d(E)", { locale: dateLocale })}
                        </span>
                      </div>
                      {overdueDays > 0 && task.status === "pending" && (
                        <div className="flex items-center gap-1 text-red-600">
                          <AlertCircle className="w-3.5 h-3.5" />
                          <span className="text-xs">
                            {language === 'ja' ? `期限から${overdueDays}日遅れ` : `${overdueDays} days overdue`}
                          </span>
                        </div>
                      )}
                      {task.status === "completed" && task.completedAt && (
                        <div className="flex items-center gap-1 text-gray-500">
                          <Clock className="w-3.5 h-3.5" />
                          <span className="text-xs">
                            {format(task.completedAt, language === 'ja' ? "M/d'完了'" : "'Completed' M/d", { locale: dateLocale })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 繰り返し、担当者 */}
                  <div className="flex items-center gap-4 flex-wrap">
                    {repeatLabel && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                        <Repeat className="w-3 h-3" />
                        {repeatLabel}
                      </span>
                    )}
                    {assignees.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        {assignees.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                          >
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs">
                              {user.name.charAt(0)}
                            </div>
                            <span>{user.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* タスク詳細モーダル */}
      {selectedTaskId && selectedTask && (
        <TaskDetailModal
          taskId={selectedTaskId}
          task={selectedTask}
          onClose={handleCloseModal}
          users={users}
          projectMembers={effectiveProjectMembers}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {/* タスク完了確認ダイアログ */}
      <CompleteTaskConfirmDialog
        open={!!confirmCompleteTask}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmCompleteTask(null);
          }
        }}
        task={confirmCompleteTask as any}
        onComplete={async (task) => {
          try {
            const result = await completeProjectTask({
              workspaceId,
              projectId: task.projectId,
              taskId: task.id,
              status: 'completed',
            }).unwrap();
            
            // Show appropriate message based on whether a new task was generated
            if (result.nextTask) {
              toast.success(t('tasks.taskCompletedWithNext') || 'Task completed! Next occurrence generated.');
            } else {
              toast.success(t('tasks.taskCompleted') || 'Task completed');
            }
            setConfirmCompleteTask(null);
            
            // Trigger refetch to update the task list with the new task
            onTaskUpdate?.();
          } catch (error) {
            handleApiError(error, t('tasks.taskCompleteFailed') || 'Failed to complete task');
          }
        }}
      />

      {/* タスク未完了確認ダイアログ */}
      <UncompleteTaskConfirmDialog
        open={!!confirmUncompleteTask}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUncompleteTask(null);
          }
        }}
        task={confirmUncompleteTask as any}
        onUncomplete={async (task) => {
          try {
            await completeProjectTask({
              workspaceId,
              projectId: task.projectId,
              taskId: task.id,
              status: 'pending',
            }).unwrap();
            toast.success(t('tasks.taskMarkedPending') || 'Task marked as pending');
            setConfirmUncompleteTask(null);
            
            // Trigger refetch to update the task list
            onTaskUpdate?.();
          } catch (error) {
            handleApiError(error, t('tasks.taskUncompleteFailed') || 'Failed to uncomplete task');
          }
        }}
      />
    </>
  );
}
